/**
 * Whether the app's own sounds are muted - one flag, shared by everything that
 * cares about it.
 *
 * A tiny observable rather than a `setMuted` method on the sound bank, because
 * the two ends of the wire have different lifetimes. The choice is made on the
 * consent dialog before any AudioContext exists, the toggle button lives for
 * the whole page, and the bank is rebuilt on every camera attach. With shared
 * state in the middle, each bank reads the current value when it builds its
 * master gain and subscribes for changes; after `dispose()` a toggle press
 * just flips the flag with no audio subscriber, and the next attach picks the
 * value up again - nothing can ever call into a dead bank.
 *
 * Deliberately not persisted. Every visit gets the consent dialog and starts
 * with sound on; a stored preference would silently skip the one moment the
 * user is told the site makes noise at all.
 *
 * NOTE: this mutes the app's output only. On iOS with `AUDIO_SESSION_TYPE`
 * left at `'auto'`, the hardware silent switch mutes independently of - and
 * invisibly to - this flag. See the comment on that constant.
 */
export interface MuteState {
  readonly muted: boolean
  set(muted: boolean): void
  /** Listeners fire on changes only, not for the current value. Returns the
   * unsubscribe. */
  subscribe(listener: (muted: boolean) => void): () => void
}

export const createMuteState = (): MuteState => {
  let muted = false
  const listeners = new Set<(muted: boolean) => void>()
  return {
    get muted() {
      return muted
    },
    set: (next) => {
      if (next === muted) return
      muted = next
      for (const listener of listeners) listener(next)
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
